import {
  WORKING_CAPITAL_INTEREST_OPTIONS,
  normalizeWorkingCapitalLabel,
} from "./interestedOpportunities.js";

const EARNINGS_IMPROVEMENT_OPTIONS = WORKING_CAPITAL_INTEREST_OPTIONS;

function parseArrayField(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return `$${num.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")}`;
}

function formatValue(value) {
  if (value == null || value === "") return null;
  if (Array.isArray(value)) {
    const items = value.filter(Boolean);
    return items.length > 0 ? items.join(", ") : null;
  }
  return String(value).trim() || null;
}

function formatEarningsInterests(notInterested) {
  const excluded = new Set(
    parseArrayField(notInterested).map((label) => normalizeWorkingCapitalLabel(label))
  );
  const interested = EARNINGS_IMPROVEMENT_OPTIONS.filter(
    (option) => !excluded.has(option)
  );
  return interested.length > 0 ? interested.join(", ") : null;
}

function inferFundingRegion(country) {
  const normalized = String(country || "").trim().toLowerCase();
  if (!normalized) return "unknown";
  if (normalized.includes("canada") || normalized === "ca") return "canada";
  if (
    normalized.includes("united states") ||
    normalized === "us" ||
    normalized === "usa"
  ) {
    return "us";
  }
  if (normalized.includes("united kingdom") || normalized === "uk") return "uk";
  return "international";
}

export function buildCompanyContextLines(companyInfo = {}) {
  const earningsInterests = formatEarningsInterests(
    companyInfo.interestedOpportunities
  );

  return [
    companyInfo.Name && `Company Name: ${companyInfo.Name}`,
    companyInfo.Domain && `Domain/Website: ${companyInfo.Domain}`,
    companyInfo.Address && `Address: ${companyInfo.Address}`,
    companyInfo.Country && `Country: ${companyInfo.Country}`,
    companyInfo.ProvinceOrState && `Province/State: ${companyInfo.ProvinceOrState}`,
    companyInfo.Industry && `Industry: ${companyInfo.Industry}`,
    companyInfo.CompanyType && `Company Type: ${companyInfo.CompanyType}`,
    companyInfo.Employees != null && `Employees: ${companyInfo.Employees}`,
    companyInfo.Founded != null && `Founded: ${companyInfo.Founded}`,
    formatCurrency(companyInfo.AnnualRevenue) &&
      `Annual Revenue: ${formatCurrency(companyInfo.AnnualRevenue)}`,
    formatCurrency(companyInfo.OperationalCosts) &&
      `Operational Costs: ${formatCurrency(companyInfo.OperationalCosts)}`,
    formatCurrency(companyInfo.AnnualRecurringRevenue) &&
      `ARR: ${formatCurrency(companyInfo.AnnualRecurringRevenue)}`,
    formatCurrency(companyInfo.MonthlyRecurringRevenue) &&
      `MRR: ${formatCurrency(companyInfo.MonthlyRecurringRevenue)}`,
    companyInfo.FiscalYearEnd && `Fiscal Year End: ${companyInfo.FiscalYearEnd}`,
    companyInfo["Profitable?"] != null &&
      companyInfo["Profitable?"] !== "" &&
      `Profitable: ${companyInfo["Profitable?"]}`,
    companyInfo.Technologies && `Technologies: ${companyInfo.Technologies}`,
    companyInfo.LinkedInUrl && `LinkedIn: ${companyInfo.LinkedInUrl}`,
    companyInfo.FollowerCount != null &&
      `LinkedIn Followers: ${companyInfo.FollowerCount}`,
    companyInfo.Description && `Short Description: ${companyInfo.Description}`,
    companyInfo.LongDescription && `Long Description: ${companyInfo.LongDescription}`,
    companyInfo.OrgChart && `Org Chart: ${companyInfo.OrgChart}`,
    companyInfo.HiringPlan && `Hiring Plan: ${companyInfo.HiringPlan}`,
    companyInfo.ProformaFinancialModel &&
      `Proforma Model: ${companyInfo.ProformaFinancialModel}`,
    earningsInterests && `Earnings Improvement Interests: ${earningsInterests}`,
    formatValue(companyInfo.governmentProgramsInterest) &&
      `Government Programs Interest: ${formatValue(companyInfo.governmentProgramsInterest)}`,
    companyInfo.PlannedHires && `Planned Hires: ${companyInfo.PlannedHires}`,
    companyInfo.LikelyCustomers && `Likely Customers: ${companyInfo.LikelyCustomers}`,
    companyInfo.TopExpenses && `Top Expenses (12 mo): ${companyInfo.TopExpenses}`,
    companyInfo.ExpensiveVendors &&
      `Expensive Vendors: ${companyInfo.ExpensiveVendors}`,
    companyInfo.GovernmentGrantsPursued &&
      `Government Grants Pursued: ${companyInfo.GovernmentGrantsPursued}`,
    companyInfo.StrategicPriorities &&
      `Strategic Priorities: ${companyInfo.StrategicPriorities}`,
    companyInfo.AdditionalInfo && `Additional Info: ${companyInfo.AdditionalInfo}`,
    companyInfo.ConversionRate != null &&
      `Conversion Rate: ${companyInfo.ConversionRate}%`,
    formatCurrency(companyInfo.AvgDealSize) &&
      `Avg Deal Size: ${formatCurrency(companyInfo.AvgDealSize)}`,
    formatCurrency(companyInfo.CustomerLifetimeValue) &&
      `Customer LTV: ${formatCurrency(companyInfo.CustomerLifetimeValue)}`,
    formatCurrency(companyInfo.Debt) && `Debt: ${formatCurrency(companyInfo.Debt)}`,
    companyInfo.AnnualTraffic != null &&
      `Annual Traffic: ${companyInfo.AnnualTraffic}`,
    companyInfo.EmailListSize != null &&
      `Email List Size: ${companyInfo.EmailListSize}`,
    formatCurrency(companyInfo.MarketingCosts) &&
      `Marketing Costs (30d): ${formatCurrency(companyInfo.MarketingCosts)}`,
    companyInfo.NewCustomersLast30Days != null &&
      `New Customers (30d): ${companyInfo.NewCustomersLast30Days}`,
    companyInfo.QualifiedLeadsLast30Days != null &&
      `Qualified Leads (30d): ${companyInfo.QualifiedLeadsLast30Days}`,
    companyInfo.MeetingsBookedLast30Days != null &&
      `Meetings Booked (30d): ${companyInfo.MeetingsBookedLast30Days}`,
    companyInfo.ProposalsSentLast30Days != null &&
      `Proposals Sent (30d): ${companyInfo.ProposalsSentLast30Days}`,
    companyInfo.DealsClosedLast30Days != null &&
      `Deals Closed (30d): ${companyInfo.DealsClosedLast30Days}`,
  ].filter(Boolean);
}

function buildRegionalGrantGuidance(region) {
  if (region === "canada") {
    return `Prioritize Canadian funding: federal programs (IRAP, SR&ED, CanExport, BDC), provincial streams matching the company's province, hiring and training credits, and clean-tech or innovation grants where relevant. Name specific program types when confidence is reasonable.`;
  }
  if (region === "us") {
    return `Prioritize US funding: federal SBIR/STTR, state economic development grants, R&D credits, workforce training incentives, and industry-specific federal programs. Note state-level fit when province/state is known.`;
  }
  if (region === "uk") {
    return `Prioritize UK funding: Innovate UK, R&D tax relief, regional growth funds, and sector-specific government support.`;
  }
  return `Identify government grants, tax credits, and non-dilutive capital programs in the company's country and region. Be explicit about geographic uncertainty.`;
}

export function buildCompanyDeepResearchPrompt(companyInfo = {}) {
  const infoLines = buildCompanyContextLines(companyInfo);
  const region = inferFundingRegion(companyInfo.Country);
  const regionalGuidance = buildRegionalGrantGuidance(region);
  const existingResearch = String(companyInfo.CompanyDeepResearch || "").trim();

  let prompt = `Conduct comprehensive deep research on the company described below. Use web search when available to verify facts, find recent news, and identify funding opportunities.

Your audience is a grants, tax credit, and government programs advisor preparing applications. Be specific, practical, and honest about uncertainty.

Write in clear markdown with these sections:
1. **Company Overview** — what they do, who they serve, stage and scale
2. **Products, Services & Value Proposition**
3. **Market & Industry Context** — trends, geography, growth signals
4. **Competitive Landscape** — likely competitors and differentiation
5. **Financial & Growth Profile** — revenue signals, profitability, hiring, traction metrics
6. **Technology & Operations**
7. **Funding & Grants Fit** — ${regionalGuidance}
8. **Tax Credits & Non-Dilutive Capital** — R&D credits, hiring credits, export programs, and similar
9. **Strategic Opportunities & Risks**
10. **Grant Application Narrative Hooks** — 5–10 bullet points a grant writer can reuse (jobs, innovation, regional impact, export, sustainability, etc.)
11. **Recent Developments** — news, launches, partnerships (cite timeframe; flag if uncertain)
12. **Research Gaps** — what should be verified manually before filing

Rules:
- Do not invent specific grant award amounts or program eligibility with false certainty.
- Flag assumptions clearly.
- Do not ask clarifying questions — deliver the full report.

--- COMPANY DATA ---
${infoLines.map((line) => `- ${line}`).join("\n")}`;

  if (existingResearch) {
    prompt += `

--- EXISTING RESEARCH (update, correct, and expand; keep accurate facts; replace outdated sections) ---
${existingResearch}`;
  }

  return prompt;
}
